import { useEffect, useState, useSyncExternalStore } from "react";
import { addDays, noonUtc, todayIn, type DayKey } from "./time";

const cache = new Map<string, Intl.DateTimeFormat>();
let hourCycle: "h12" | "h23" | undefined;
/** Format de l'heure choisi dans le profil ; « auto » suit la langue. */
export function setTimeFormat(pref: "auto" | "12" | "24" | undefined) {
  hourCycle = pref === "12" ? "h12" : pref === "24" ? "h23" : undefined;
}

export function dtf(locale: string, base: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const options = hourCycle && base.hour && !base.timeStyle ? { ...base, hourCycle } : base;
  const key = `${locale}|${JSON.stringify(options)}`;
  let f = cache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, options);
    cache.set(key, f);
  }
  return f;
}

/** Libellé d'un jour (DayKey), indépendant du fuseau. */
export const dayFmt = (key: DayKey, locale: string, options: Intl.DateTimeFormatOptions) =>
  dtf(locale, { ...options, timeZone: "UTC" }).format(noonUtc(key));

export const timeFmt = (iso: string, locale: string, tz: string) =>
  dtf(locale, { hour: "numeric", minute: "2-digit", timeZone: tz }).format(new Date(iso));

export const hourFmt = (hour: number, locale: string) =>
  dtf(locale, { hour: "numeric", timeZone: "UTC" }).format(new Date(Date.UTC(2026, 0, 5, hour)));

/** « octobre 2026 », ou « oct. – nov. 2026 » quand la plage chevauche deux mois. */
export function monthRangeTitle(first: DayKey, last: DayKey, locale: string) {
  if (first.slice(0, 7) === last.slice(0, 7)) return dayFmt(first, locale, { month: "long", year: "numeric" });
  return dtf(locale, { month: "short", year: "numeric", timeZone: "UTC" }).formatRange(noonUtc(first), noonUtc(last));
}

/** Plage lisible d'un événement pour la fiche détaillée. */
export function eventWhen(start: string, end: string, allDay: boolean, locale: string, tz: string) {
  if (allDay) {
    const first = start.slice(0, 10);
    const last = addDays(end.slice(0, 10), -1);
    const f = dtf(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
    return last <= first ? f.format(noonUtc(first)) : f.formatRange(noonUtc(first), noonUtc(last));
  }
  return dtf(locale, { weekday: "long", day: "numeric", month: "long", hour: "numeric", minute: "2-digit", timeZone: tz }).formatRange(
    new Date(start),
    new Date(end),
  );
}

/** Date du jour dans le fuseau de l'utilisateur, mise à jour au passage de minuit. */
export function useToday(tz: string): DayKey {
  const [today, setToday] = useState(() => todayIn(tz));
  useEffect(() => {
    setToday(todayIn(tz));
    const id = setInterval(() => setToday(todayIn(tz)), 60_000);
    return () => clearInterval(id);
  }, [tz]);
  return today;
}

/** Instant courant, rafraîchi chaque minute (ligne « maintenant »). */
export function useNow(): number {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (cb) => {
      const m = window.matchMedia(query);
      m.addEventListener("change", cb);
      return () => m.removeEventListener("change", cb);
    },
    () => window.matchMedia(query).matches,
  );
}
